import { supabase } from '../lib/supabase';

// Articles Service
export const articlesService = {
  async getAll(filters = {}) {
    try {
      let query = supabase?.from('articles')?.select('*, user_profiles(username, full_name)')?.order('created_at', { ascending: false });

      if (filters?.published !== undefined) {
        query = query?.eq('published', filters?.published);
      }
      
      if (filters?.category) {
        query = query?.eq('category', filters?.category);
      }

      if (filters?.search) {
        query = query?.or(`title_ar.ilike.%${filters?.search}%,title_en.ilike.%${filters?.search}%,title_tr.ilike.%${filters?.search}%`);
      }

      const { data, error } = await query;
      if (error) throw error;

      return data?.map(article => ({
        id: article?.id,
        titleAr: article?.title_ar,
        titleEn: article?.title_en,
        titleTr: article?.title_tr,
        contentAr: article?.content_ar,
        contentEn: article?.content_en,
        contentTr: article?.content_tr,
        excerptAr: article?.excerpt_ar,
        excerptEn: article?.excerpt_en,
        excerptTr: article?.excerpt_tr,
        imageUrl: article?.image_url,
        category: article?.category,
        published: article?.published,
        date: article?.date,
        formattedDateAr: article?.formatted_date_ar,
        formattedDateEn: article?.formatted_date_en,
        formattedDateTr: article?.formatted_date_tr,
        authorId: article?.author_id,
        author: article?.user_profiles ? {
          username: article?.user_profiles?.username,
          fullName: article?.user_profiles?.full_name
        } : null,
        createdAt: article?.created_at,
        updatedAt: article?.updated_at
      }));
    } catch (error) {
      console.error('Error fetching articles:', error);
      throw error;
    }
  },

  async getById(id) {
    try {
      const { data, error } = await supabase?.from('articles')?.select('*, user_profiles(username, full_name)')?.eq('id', id)?.single();

      if (error) throw error;

      return {
        id: data?.id,
        titleAr: data?.title_ar,
        titleEn: data?.title_en,
        titleTr: data?.title_tr,
        contentAr: data?.content_ar,
        contentEn: data?.content_en,
        contentTr: data?.content_tr,
        excerptAr: data?.excerpt_ar,
        excerptEn: data?.excerpt_en,
        excerptTr: data?.excerpt_tr,
        imageUrl: data?.image_url,
        category: data?.category,
        published: data?.published,
        date: data?.date,
        formattedDateAr: data?.formatted_date_ar,
        formattedDateEn: data?.formatted_date_en,
        formattedDateTr: data?.formatted_date_tr,
        authorId: data?.author_id,
        author: data?.user_profiles ? {
          username: data?.user_profiles?.username,
          fullName: data?.user_profiles?.full_name
        } : null,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at
      };
    } catch (error) {
      console.error('Error fetching article:', error);
      throw error;
    }
  },

  async create(article) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase?.from('articles')?.insert({
          title_ar: article?.titleAr,
          title_en: article?.titleEn,
          title_tr: article?.titleTr,
          content_ar: article?.contentAr,
          content_en: article?.contentEn,
          content_tr: article?.contentTr,
          excerpt_ar: article?.excerptAr,
          excerpt_en: article?.excerptEn,
          excerpt_tr: article?.excerptTr,
          image_url: article?.imageUrl,
          category: article?.category,
          published: article?.published || false,
          date: article?.date,
          formatted_date_ar: article?.formattedDateAr,
          formatted_date_en: article?.formattedDateEn,
          formatted_date_tr: article?.formattedDateTr,
          author_id: user?.id
        })?.select()?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error creating article:', error);
      throw error;
    }
  },

  async update(id, article) {
    try {
      const { data, error } = await supabase?.from('articles')?.update({
          title_ar: article?.titleAr,
          title_en: article?.titleEn,
          title_tr: article?.titleTr,
          content_ar: article?.contentAr,
          content_en: article?.contentEn,
          content_tr: article?.contentTr,
          excerpt_ar: article?.excerptAr,
          excerpt_en: article?.excerptEn,
          excerpt_tr: article?.excerptTr,
          image_url: article?.imageUrl,
          category: article?.category,
          published: article?.published,
          date: article?.date,
          formatted_date_ar: article?.formattedDateAr,
          formatted_date_en: article?.formattedDateEn,
          formatted_date_tr: article?.formattedDateTr,
          updated_at: new Date()?.toISOString()
        })?.eq('id', id)?.select()?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating article:', error);
      throw error;
    }
  },

  async delete(id) {
    try {
      const { error } = await supabase?.from('articles')?.delete()?.eq('id', id);

      if (error) throw error;
    } catch (error) {
      console.error('Error deleting article:', error);
      throw error;
    }
  }
};

// Cities Service
export const citiesService = {
  async getAll() {
    try {
      const { data, error } = await supabase?.from('cities')?.select('*')?.order('name_en', { ascending: true });

      if (error) throw error;

      return data?.map(city => ({
        id: city?.id,
        nameAr: city?.name_ar,
        nameEn: city?.name_en,
        nameTr: city?.name_tr,
        cityCode: city?.city_code,
        svgX: city?.svg_x,
        svgY: city?.svg_y,
        clickZone: city?.click_zone,
        colorZone: city?.color_zone,
        createdAt: city?.created_at,
        updatedAt: city?.updated_at
      }));
    } catch (error) {
      console.error('Error fetching cities:', error);
      throw error;
    }
  },

  async getById(id) {
    try {
      const { data, error } = await supabase?.from('cities')?.select('*')?.eq('id', id)?.single();

      if (error) throw error;

      return {
        id: data?.id,
        nameAr: data?.name_ar,
        nameEn: data?.name_en,
        nameTr: data?.name_tr,
        cityCode: data?.city_code,
        svgX: data?.svg_x,
        svgY: data?.svg_y,
        clickZone: data?.click_zone,
        colorZone: data?.color_zone,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at
      };
    } catch (error) {
      console.error('Error fetching city:', error);
      throw error;
    }
  },

  async create(city) {
    try {
      const { data, error } = await supabase?.from('cities')?.insert({
          name_ar: city?.nameAr,
          name_en: city?.nameEn,
          name_tr: city?.nameTr,
          city_code: city?.cityCode,
          svg_x: city?.svgX,
          svg_y: city?.svgY,
          click_zone: city?.clickZone,
          color_zone: city?.colorZone
        })?.select()?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error creating city:', error);
      throw error;
    }
  },

  async update(id, city) {
    try {
      const { data, error } = await supabase?.from('cities')?.update({
          name_ar: city?.nameAr,
          name_en: city?.nameEn,
          name_tr: city?.nameTr,
          city_code: city?.cityCode,
          svg_x: city?.svgX,
          svg_y: city?.svgY,
          click_zone: city?.clickZone,
          color_zone: city?.colorZone,
          updated_at: new Date()?.toISOString()
        })?.eq('id', id)?.select()?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating city:', error);
      throw error;
    }
  },

  async delete(id) {
    try {
      const { error } = await supabase?.from('cities')?.delete()?.eq('id', id);

      if (error) throw error;
    } catch (error) {
      console.error('Error deleting city:', error);
      throw error;
    }
  }
};

// Projects Service
export const projectsService = {
  async getAll(filters = {}) {
    try {
      let query = supabase?.from('projects')?.select('*')?.order('created_at', { ascending: false });

      if (filters?.status) {
        query = query?.eq('status', filters?.status);
      }

      if (filters?.search) {
        query = query?.or(`title_ar.ilike.%${filters?.search}%,title_en.ilike.%${filters?.search}%,title_tr.ilike.%${filters?.search}%`);
      }

      const { data, error } = await query;
      if (error) throw error;

      return data?.map(project => ({
        id: project?.id,
        titleAr: project?.title_ar,
        titleEn: project?.title_en,
        titleTr: project?.title_tr,
        descriptionAr: project?.description_ar,
        descriptionEn: project?.description_en,
        descriptionTr: project?.description_tr,
        categoryAr: project?.category_ar,
        categoryEn: project?.category_en,
        categoryTr: project?.category_tr,
        imageUrl: project?.image_url,
        status: project?.status,
        progress: project?.progress,
        memberCount: project?.member_count,
        startDate: project?.start_date,
        endDate: project?.end_date,
        createdAt: project?.created_at,
        updatedAt: project?.updated_at
      }));
    } catch (error) {
      console.error('Error fetching projects:', error);
      throw error;
    }
  },

  async getById(id) {
    try {
      const { data, error } = await supabase?.from('projects')?.select('*')?.eq('id', id)?.single();

      if (error) throw error;

      return {
        id: data?.id,
        titleAr: data?.title_ar,
        titleEn: data?.title_en,
        titleTr: data?.title_tr,
        descriptionAr: data?.description_ar,
        descriptionEn: data?.description_en,
        descriptionTr: data?.description_tr,
        categoryAr: data?.category_ar,
        categoryEn: data?.category_en,
        categoryTr: data?.category_tr,
        imageUrl: data?.image_url,
        status: data?.status,
        progress: data?.progress,
        memberCount: data?.member_count,
        startDate: data?.start_date,
        endDate: data?.end_date,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at
      };
    } catch (error) {
      console.error('Error fetching project:', error);
      throw error;
    }
  },

  async create(project) {
    try {
      const { data, error } = await supabase?.from('projects')?.insert({
          title_ar: project?.titleAr,
          title_en: project?.titleEn,
          title_tr: project?.titleTr,
          description_ar: project?.descriptionAr,
          description_en: project?.descriptionEn,
          description_tr: project?.descriptionTr,
          category_ar: project?.categoryAr,
          category_en: project?.categoryEn,
          category_tr: project?.categoryTr,
          image_url: project?.imageUrl,
          status: project?.status || 'planned',
          progress: project?.progress || 0,
          member_count: project?.memberCount || 0,
          start_date: project?.startDate,
          end_date: project?.endDate
        })?.select()?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error creating project:', error);
      throw error;
    }
  },

  async update(id, project) {
    try {
      const { data, error } = await supabase?.from('projects')?.update({
          title_ar: project?.titleAr,
          title_en: project?.titleEn,
          title_tr: project?.titleTr,
          description_ar: project?.descriptionAr,
          description_en: project?.descriptionEn,
          description_tr: project?.descriptionTr,
          category_ar: project?.categoryAr,
          category_en: project?.categoryEn,
          category_tr: project?.categoryTr,
          image_url: project?.imageUrl,
          status: project?.status,
          progress: project?.progress,
          member_count: project?.memberCount,
          start_date: project?.startDate,
          end_date: project?.endDate,
          updated_at: new Date()?.toISOString()
        })?.eq('id', id)?.select()?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating project:', error);
      throw error;
    }
  },

  async delete(id) {
    try {
      const { error } = await supabase?.from('projects')?.delete()?.eq('id', id);

      if (error) throw error;
    } catch (error) {
      console.error('Error deleting project:', error);
      throw error;
    }
  }
};

// Offices Service
export const officesService = {
  async getAll(filters = {}) {
    try {
      let query = supabase?.from('offices')?.select('*')?.order('order_index', { ascending: true });

      if (filters?.category) {
        query = query?.eq('category', filters?.category);
      }

      const { data, error } = await query;
      if (error) throw error;

      return data?.map(office => ({
        id: office?.id,
        nameAr: office?.name_ar,
        nameEn: office?.name_en,
        nameTr: office?.name_tr,
        headNameAr: office?.head_name_ar,
        headNameEn: office?.head_name_en,
        headNameTr: office?.head_name_tr,
        positionAr: office?.position_ar,
        positionEn: office?.position_en,
        positionTr: office?.position_tr,
        email: office?.email,
        phone: office?.phone,
        imageUrl: office?.image_url,
        category: office?.category,
        orderIndex: office?.order_index,
        createdAt: office?.created_at,
        updatedAt: office?.updated_at
      }));
    } catch (error) {
      console.error('Error fetching offices:', error);
      throw error;
    }
  },

  async getById(id) {
    try {
      const { data, error } = await supabase?.from('offices')?.select('*')?.eq('id', id)?.single();

      if (error) throw error;

      return {
        id: data?.id,
        nameAr: data?.name_ar,
        nameEn: data?.name_en,
        nameTr: data?.name_tr,
        headNameAr: data?.head_name_ar,
        headNameEn: data?.head_name_en,
        headNameTr: data?.head_name_tr,
        positionAr: data?.position_ar,
        positionEn: data?.position_en,
        positionTr: data?.position_tr,
        email: data?.email,
        phone: data?.phone,
        imageUrl: data?.image_url,
        category: data?.category,
        orderIndex: data?.order_index,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at
      };
    } catch (error) {
      console.error('Error fetching office:', error);
      throw error;
    }
  },

  async create(office) {
    try {
      const { data, error } = await supabase?.from('offices')?.insert({
          name_ar: office?.nameAr,
          name_en: office?.nameEn,
          name_tr: office?.nameTr,
          head_name_ar: office?.headNameAr,
          head_name_en: office?.headNameEn,
          head_name_tr: office?.headNameTr,
          position_ar: office?.positionAr,
          position_en: office?.positionEn,
          position_tr: office?.positionTr,
          email: office?.email,
          phone: office?.phone,
          image_url: office?.imageUrl,
          category: office?.category,
          order_index: office?.orderIndex || 0
        })?.select()?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error creating office:', error);
      throw error;
    }
  },

  async update(id, office) {
    try {
      const { data, error } = await supabase?.from('offices')?.update({
          name_ar: office?.nameAr,
          name_en: office?.nameEn,
          name_tr: office?.nameTr,
          head_name_ar: office?.headNameAr,
          head_name_en: office?.headNameEn,
          head_name_tr: office?.headNameTr,
          position_ar: office?.positionAr,
          position_en: office?.positionEn,
          position_tr: office?.positionTr,
          email: office?.email,
          phone: office?.phone,
          image_url: office?.imageUrl,
          category: office?.category,
          order_index: office?.orderIndex,
          updated_at: new Date()?.toISOString()
        })?.eq('id', id)?.select()?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating office:', error);
      throw error;
    }
  },

  async delete(id) {
    try {
      const { error } = await supabase?.from('offices')?.delete()?.eq('id', id);

      if (error) throw error;
    } catch (error) {
      console.error('Error deleting office:', error);
      throw error;
    }
  }
};

// Sub Unions Service
export const subUnionsService = {
  async getAll(filters = {}) {
    try {
      let query = supabase?.from('sub_unions')?.select('*, cities(name_ar, name_en, name_tr)')?.order('created_at', { ascending: false });

      if (filters?.status) {
        query = query?.eq('status', filters?.status);
      }

      if (filters?.cityId) {
        query = query?.eq('city_id', filters?.cityId);
      }

      if (filters?.search) {
        query = query?.or(`name_ar.ilike.%${filters?.search}%,name_en.ilike.%${filters?.search}%,name_tr.ilike.%${filters?.search}%`);
      }

      const { data, error } = await query;
      if (error) throw error;

      return data?.map(union => ({
        id: union?.id,
        nameAr: union?.name_ar,
        nameEn: union?.name_en,
        nameTr: union?.name_tr,
        phone: union?.phone,
        contactEmail: union?.contact_email,
        cityId: union?.city_id,
        city: union?.cities ? {
          nameAr: union?.cities?.name_ar,
          nameEn: union?.cities?.name_en,
          nameTr: union?.cities?.name_tr
        } : null,
        status: union?.status,
        memberCount: union?.member_count,
        establishedYear: union?.established_year,
        logoUrl: union?.logo_url,
        contactNumberAr: union?.contact_number_ar,
        contactNumberEn: union?.contact_number_en,
        contactNumberTr: union?.contact_number_tr,
        cardDescriptionAr: union?.card_description_ar,
        cardDescriptionEn: union?.card_description_en,
        cardDescriptionTr: union?.card_description_tr,
        aboutDescriptionAr: union?.about_description_ar,
        aboutDescriptionEn: union?.about_description_en,
        aboutDescriptionTr: union?.about_description_tr,
        specialAchievementsAr: union?.special_achievements_ar,
        specialAchievementsEn: union?.special_achievements_en,
        specialAchievementsTr: union?.special_achievements_tr,
        activitiesAndAchievementsAr: union?.activities_and_achievements_ar,
        activitiesAndAchievementsEn: union?.activities_and_achievements_en,
        activitiesAndAchievementsTr: union?.activities_and_achievements_tr,
        currentAdministrativeTeamAr: union?.current_administrative_team_ar,
        currentAdministrativeTeamEn: union?.current_administrative_team_en,
        currentAdministrativeTeamTr: union?.current_administrative_team_tr,
        notesAr: union?.notes_ar,
        notesEn: union?.notes_en,
        notesTr: union?.notes_tr,
        createdAt: union?.created_at,
        updatedAt: union?.updated_at
      }));
    } catch (error) {
      console.error('Error fetching sub unions:', error);
      throw error;
    }
  },

  async getById(id) {
    try {
      const { data, error } = await supabase?.from('sub_unions')?.select('*, cities(name_ar, name_en, name_tr)')?.eq('id', id)?.single();

      if (error) throw error;

      return {
        id: data?.id,
        nameAr: data?.name_ar,
        nameEn: data?.name_en,
        nameTr: data?.name_tr,
        phone: data?.phone,
        contactEmail: data?.contact_email,
        cityId: data?.city_id,
        city: data?.cities ? {
          nameAr: data?.cities?.name_ar,
          nameEn: data?.cities?.name_en,
          nameTr: data?.cities?.name_tr
        } : null,
        status: data?.status,
        memberCount: data?.member_count,
        establishedYear: data?.established_year,
        logoUrl: data?.logo_url,
        contactNumberAr: data?.contact_number_ar,
        contactNumberEn: data?.contact_number_en,
        contactNumberTr: data?.contact_number_tr,
        cardDescriptionAr: data?.card_description_ar,
        cardDescriptionEn: data?.card_description_en,
        cardDescriptionTr: data?.card_description_tr,
        aboutDescriptionAr: data?.about_description_ar,
        aboutDescriptionEn: data?.about_description_en,
        aboutDescriptionTr: data?.about_description_tr,
        specialAchievementsAr: data?.special_achievements_ar,
        specialAchievementsEn: data?.special_achievements_en,
        specialAchievementsTr: data?.special_achievements_tr,
        activitiesAndAchievementsAr: data?.activities_and_achievements_ar,
        activitiesAndAchievementsEn: data?.activities_and_achievements_en,
        activitiesAndAchievementsTr: data?.activities_and_achievements_tr,
        currentAdministrativeTeamAr: data?.current_administrative_team_ar,
        currentAdministrativeTeamEn: data?.current_administrative_team_en,
        currentAdministrativeTeamTr: data?.current_administrative_team_tr,
        notesAr: data?.notes_ar,
        notesEn: data?.notes_en,
        notesTr: data?.notes_tr,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at
      };
    } catch (error) {
      console.error('Error fetching sub union:', error);
      throw error;
    }
  },

  async create(union) {
    try {
      const { data, error } = await supabase?.from('sub_unions')?.insert({
          name_ar: union?.nameAr,
          name_en: union?.nameEn,
          name_tr: union?.nameTr,
          phone: union?.phone,
          contact_email: union?.contactEmail,
          city_id: union?.cityId,
          status: union?.status || 'active',
          member_count: union?.memberCount || 0,
          established_year: union?.establishedYear,
          logo_url: union?.logoUrl,
          contact_number_ar: union?.contactNumberAr,
          contact_number_en: union?.contactNumberEn,
          contact_number_tr: union?.contactNumberTr,
          card_description_ar: union?.cardDescriptionAr,
          card_description_en: union?.cardDescriptionEn,
          card_description_tr: union?.cardDescriptionTr,
          about_description_ar: union?.aboutDescriptionAr,
          about_description_en: union?.aboutDescriptionEn,
          about_description_tr: union?.aboutDescriptionTr,
          special_achievements_ar: union?.specialAchievementsAr,
          special_achievements_en: union?.specialAchievementsEn,
          special_achievements_tr: union?.specialAchievementsTr,
          activities_and_achievements_ar: union?.activitiesAndAchievementsAr,
          activities_and_achievements_en: union?.activitiesAndAchievementsEn,
          activities_and_achievements_tr: union?.activitiesAndAchievementsTr,
          current_administrative_team_ar: union?.currentAdministrativeTeamAr,
          current_administrative_team_en: union?.currentAdministrativeTeamEn,
          current_administrative_team_tr: union?.currentAdministrativeTeamTr,
          notes_ar: union?.notesAr,
          notes_en: union?.notesEn,
          notes_tr: union?.notesTr
        })?.select()?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error creating sub union:', error);
      throw error;
    }
  },

  async update(id, union) {
    try {
      const { data, error } = await supabase?.from('sub_unions')?.update({
          name_ar: union?.nameAr,
          name_en: union?.nameEn,
          name_tr: union?.nameTr,
          phone: union?.phone,
          contact_email: union?.contactEmail,
          city_id: union?.cityId,
          status: union?.status,
          member_count: union?.memberCount,
          established_year: union?.establishedYear,
          logo_url: union?.logoUrl,
          contact_number_ar: union?.contactNumberAr,
          contact_number_en: union?.contactNumberEn,
          contact_number_tr: union?.contactNumberTr,
          card_description_ar: union?.cardDescriptionAr,
          card_description_en: union?.cardDescriptionEn,
          card_description_tr: union?.cardDescriptionTr,
          about_description_ar: union?.aboutDescriptionAr,
          about_description_en: union?.aboutDescriptionEn,
          about_description_tr: union?.aboutDescriptionTr,
          special_achievements_ar: union?.specialAchievementsAr,
          special_achievements_en: union?.specialAchievementsEn,
          special_achievements_tr: union?.specialAchievementsTr,
          activities_and_achievements_ar: union?.activitiesAndAchievementsAr,
          activities_and_achievements_en: union?.activitiesAndAchievementsEn,
          activities_and_achievements_tr: union?.activitiesAndAchievementsTr,
          current_administrative_team_ar: union?.currentAdministrativeTeamAr,
          current_administrative_team_en: union?.currentAdministrativeTeamEn,
          current_administrative_team_tr: union?.currentAdministrativeTeamTr,
          notes_ar: union?.notesAr,
          notes_en: union?.notesEn,
          notes_tr: union?.notesTr,
          updated_at: new Date()?.toISOString()
        })?.eq('id', id)?.select()?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating sub union:', error);
      throw error;
    }
  },

  async delete(id) {
    try {
      const { error } = await supabase?.from('sub_unions')?.delete()?.eq('id', id);

      if (error) throw error;
    } catch (error) {
      console.error('Error deleting sub union:', error);
      throw error;
    }
  }
};

// Members Service  
export const membersService = {
  async getAll(filters = {}) {
    try {
      let query = supabase?.from('members')?.select('*')?.order('full_name', { ascending: true });

      if (filters?.city) {
        query = query?.eq('city', filters?.city);
      }

      if (filters?.universityName) {
        query = query?.eq('university_name', filters?.universityName);
      }

      if (filters?.search) {
        query = query?.or(`full_name.ilike.%${filters?.search}%,academic_branch.ilike.%${filters?.search}%,university_name.ilike.%${filters?.search}%`);
      }

      const { data, error } = await query;
      if (error) throw error;

      return data?.map(member => ({
        id: member?.id,
        fullName: member?.full_name,
        city: member?.city,
        academicBranch: member?.academic_branch,
        universityName: member?.university_name
      }));
    } catch (error) {
      console.error('Error fetching members:', error);
      throw error;
    }
  },

  async getById(id) {
    try {
      const { data, error } = await supabase?.from('members')?.select('*')?.eq('id', id)?.single();

      if (error) throw error;

      return {
        id: data?.id,
        fullName: data?.full_name,
        city: data?.city,
        academicBranch: data?.academic_branch,
        universityName: data?.university_name
      };
    } catch (error) {
      console.error('Error fetching member:', error);
      throw error;
    }
  },

  async create(member) {
    try {
      const { data, error } = await supabase?.from('members')?.insert({
          full_name: member?.fullName,
          city: member?.city,
          academic_branch: member?.academicBranch,
          university_name: member?.universityName
        })?.select()?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error creating member:', error);
      throw error;
    }
  },

  async update(id, member) {
    try {
      const { data, error } = await supabase?.from('members')?.update({
          full_name: member?.fullName,
          city: member?.city,
          academic_branch: member?.academicBranch,
          university_name: member?.universityName
        })?.eq('id', id)?.select()?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating member:', error);
      throw error;
    }
  },

  async delete(id) {
    try {
      const { error } = await supabase?.from('members')?.delete()?.eq('id', id);

      if (error) throw error;
    } catch (error) {
      console.error('Error deleting member:', error);
      throw error;
    }
  }
};